/**
 * Idle-session timing, as pure functions.
 *
 * Every decision about whether a session has gone idle is made here, from a
 * timestamp passed in rather than read from a clock. That is what makes a
 * 30-minute timeout testable in microseconds, and it is also why this file
 * holds no timers, no listeners and no React: `vitest.config.ts` runs in
 * `environment: 'node'` and only collects `src/**\/*.test.ts`, so logic that
 * lives in a component or a hook is logic with no test.
 *
 * The companion module `idleActivity.ts` owns *when* the clock is reset; this
 * one owns what the elapsed time means.
 */

/**
 * Idle period applied when the instance has configured none: 24 hours.
 *
 * Not policy — the ticket proposed 30 minutes and records the number as owned
 * by QA/Regulatory. 24 hours is the product call for now, and it is a long way
 * from what an unattended-workstation control would normally be, so it is worth
 * confirming before this ships to a regulated environment.
 *
 * The constants live in one file precisely so that decision costs one edit and
 * cannot drift between the warning and the sign-out.
 */
export const DEFAULT_IDLE_MINUTES = 24 * 60

/** How long before sign-out the warning appears. */
export const WARN_BEFORE_MS = 2 * 60 * 1000

/**
 * The configurable range, mirroring `UpdateNetworkDto` on the backend.
 *
 * Duplicated deliberately rather than shared: the two repos ship separately, so
 * a frontend that trusted the server to have validated would render a broken
 * countdown against an older API. `clampIdleMinutes` is the guard.
 */
export const MIN_IDLE_MINUTES = 1
export const MAX_IDLE_MINUTES = 24 * 60

export type IdleState = 'active' | 'warning' | 'expired'

export interface IdleStatus {
  state: IdleState
  /** Milliseconds until sign-out. Zero once expired, never negative. */
  msRemaining: number
}

export interface EvaluateIdleInput {
  now: number
  /** When the user last interacted, epoch ms. Null means "not started yet". */
  lastActivityAt: number | null
  idleMs: number
  warnMs?: number
}

/**
 * Resolve the configured value into minutes actually used.
 *
 * Applies the "not configured" fallback and clamps anything out of range. The
 * clamp is not paranoia about our own UI — the column has no CHECK constraint,
 * so a value written directly by SQL reaches this function unvalidated, and
 * both failure modes are bad: a tiny value signs people out mid-task, a huge
 * one silently disables the protection.
 */
export function resolveIdleMinutes(configured: number | null | undefined): number {
  if (configured === null || configured === undefined) return DEFAULT_IDLE_MINUTES
  if (!Number.isFinite(configured)) return DEFAULT_IDLE_MINUTES
  return clampIdleMinutes(configured)
}

export function clampIdleMinutes(minutes: number): number {
  return Math.min(MAX_IDLE_MINUTES, Math.max(MIN_IDLE_MINUTES, Math.round(minutes)))
}

/** Convenience for callers holding the configured value. */
export function resolveIdleMs(configured: number | null | undefined): number {
  return resolveIdleMinutes(configured) * 60 * 1000
}

/**
 * Where the session stands right now.
 *
 * Derived from a stored timestamp rather than a countdown, which is what makes
 * the sleep case correct: a `setTimeout` scheduled for 30 minutes does not fire
 * while a laptop is asleep, so a timer-based implementation would wake up and
 * grant the user a fresh window. Comparing `now` against the last activity
 * instead means a machine that slept past the threshold is already `expired` on
 * the first evaluation after waking.
 *
 * A null `lastActivityAt` reports `active` with a full window: it means the
 * clock has not been started yet (a tab that just mounted), and treating an
 * unknown as an expiry would sign people out on load.
 */
export function evaluateIdle({
  now,
  lastActivityAt,
  idleMs,
  warnMs = WARN_BEFORE_MS,
}: EvaluateIdleInput): IdleStatus {
  if (lastActivityAt === null) return { state: 'active', msRemaining: idleMs }

  const msRemaining = lastActivityAt + idleMs - now
  if (msRemaining <= 0) return { state: 'expired', msRemaining: 0 }

  // A warning window at least as long as the whole period would mean the
  // warning is showing from the moment the clock starts, so it is capped. This
  // is reachable: an admin may set a 1-minute timeout against a 2-minute
  // warning, and the test suite pins it.
  const effectiveWarnMs = Math.min(warnMs, idleMs)
  if (msRemaining <= effectiveWarnMs) return { state: 'warning', msRemaining }

  return { state: 'active', msRemaining }
}

/**
 * Whole seconds left, for the countdown the warning renders.
 *
 * Rounds up so the display reaches "0" exactly when the session ends rather
 * than sitting on it for a second first.
 */
export function secondsRemaining(msRemaining: number): number {
  return Math.max(0, Math.ceil(msRemaining / 1000))
}

/** `m:ss`, the countdown format in the warning dialog. */
export function formatCountdown(msRemaining: number): string {
  const total = secondsRemaining(msRemaining)
  const minutes = Math.floor(total / 60)
  const seconds = total % 60
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

/**
 * The periods offered in the settings screen.
 *
 * A fixed ladder rather than a free-text minutes box: an admin thinks in "an
 * hour" or "a working day", not in 480, and every hand-typed number is a chance
 * to fat-finger a security control into 5 minutes or 5 days. The backend still
 * accepts any value in range, so a figure set by an earlier build or by hand is
 * shown as-is rather than being silently snapped to the nearest option.
 *
 * Spans minutes and hours only: the ceiling is 24 hours, so a day unit would
 * offer exactly one value.
 */
export const IDLE_PRESET_MINUTES = [15, 30, 60, 120, 360, 720, 1440] as const

const MINUTES_PER_HOUR = 60

function plural(n: number, unit: string): string {
  return `${n} ${n === 1 ? unit : `${unit}s`}`
}

/**
 * A period as a person would say it: `45 minutes`, `1 hour`, `24 hours`.
 *
 * Pluralisation is explicit because "1 hours" in a settings screen looks like a
 * bug in everything else on the page.
 */
export function formatIdleDuration(minutes: number): string {
  if (minutes < MINUTES_PER_HOUR) return plural(minutes, 'minute')

  const hours = Math.floor(minutes / MINUTES_PER_HOUR)
  const rest = minutes % MINUTES_PER_HOUR
  if (rest === 0) return plural(hours, 'hour')

  return `${plural(hours, 'hour')} ${plural(rest, 'minute')}`
}

/** The units the custom field offers, largest last. */
export const IDLE_UNITS = [
  { id: 'minutes', minutes: 1 },
  { id: 'hours', minutes: MINUTES_PER_HOUR },
] as const

export type IdleUnit = (typeof IDLE_UNITS)[number]['id']

export function toMinutes(value: number, unit: IdleUnit): number {
  return value * (IDLE_UNITS.find((u) => u.id === unit)?.minutes ?? 1)
}

/**
 * Split a period into the largest unit that divides it exactly.
 *
 * Used to seed the custom field from a stored value, so reopening the screen on
 * 180 offers "3 hours" rather than "180 minutes" — the number the admin
 * originally typed, not the one the database happens to hold.
 */
export function splitDuration(minutes: number): { value: number; unit: IdleUnit } {
  for (const unit of [...IDLE_UNITS].reverse()) {
    if (minutes % unit.minutes === 0) return { value: minutes / unit.minutes, unit: unit.id }
  }
  return { value: minutes, unit: 'minutes' }
}
